import React, { useState, useEffect } from 'react';
import { Bell, AlertCircle, Megaphone } from 'lucide-react';
import api from '../../api';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function ParentAnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/announcements')
      .then(d => { if (d.success) setAnnouncements(d.data); })
      .catch(() => setAnnouncements([]))
      .finally(() => setLoading(false));
  }, []);

  // Group by school_name
  const grouped = announcements.reduce((acc, ann) => {
    const key = ann.school_name || 'My School';
    if (!acc[key]) acc[key] = [];
    acc[key].push(ann);
    return acc;
  }, {});
  const hasMultipleSchools = Object.keys(grouped).length > 1;

  return (
    <div className="p-4 lg:p-6 max-w-3xl mx-auto space-y-5" data-testid="parent-announcements-page">
      <div>
        <h1 className="text-2xl font-black text-slate-900">Announcements</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          {hasMultipleSchools ? 'Messages from all schools grouped by school' : 'Messages from the school administration'}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 py-16 text-center shadow-sm">
          <Megaphone className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="font-bold text-slate-500">No announcements yet</p>
          <p className="text-sm text-slate-400 mt-1">School announcements will appear here</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([schoolName, anns]) => (
            <div key={schoolName}>
              {hasMultipleSchools && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-slate-100" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-3 bg-slate-50 rounded-full py-1 border border-slate-100">
                    {schoolName}
                  </span>
                  <div className="h-px flex-1 bg-slate-100" />
                </div>
              )}
              <div className="space-y-3">
                {anns.map(ann => (
                  <div
                    key={ann.id}
                    className={`bg-white rounded-3xl border p-5 shadow-sm hover:shadow-md transition-shadow ${
                      ann.priority === 'urgent' ? 'border-red-200' : 'border-slate-100'
                    }`}
                    data-testid={`ann-${ann.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                        ann.priority === 'urgent' ? 'bg-red-100' : 'bg-blue-50'
                      }`}>
                        {ann.priority === 'urgent'
                          ? <AlertCircle className="w-5 h-5 text-red-600" />
                          : <Bell className="w-5 h-5 text-[#2563eb]" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-bold text-slate-800 text-sm">{ann.title}</p>
                          <span className="text-xs text-slate-400 flex-shrink-0">{timeAgo(ann.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{ann.message || ann.content}</p>
                        {ann.priority === 'urgent' && (
                          <span className="inline-block mt-2 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Urgent</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
